import { FC, useState, useEffect, useRef, useCallback, FocusEvent } from "react";
import { Stack, Typography, Box, styled } from "@mui/material";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { IconChevronDown } from "@/assets/icons";
import { Select } from "@/components/elements/Select";
import { black, background } from "@/theme/colors";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { TOption } from "@/types/common";

type TPerformanceDetailHeaderProps = {
  targetYear: Dayjs;
  onYearChange: (year: Dayjs | null) => void;
  categoryOptions: TOption[];
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  periodLabel: string;
};

export const PerformanceDetailHeader: FC<TPerformanceDetailHeaderProps> = ({
  targetYear,
  onYearChange,
  categoryOptions,
  selectedCategory,
  onCategoryChange,
  periodLabel,
}) => {
  const [localYear, setLocalYear] = useState<Dayjs>(targetYear);
  const [pickerKey, setPickerKey] = useState(0);
  const yearContainerRef = useRef<HTMLDivElement>(null);
  const categoryContainerRef = useRef<HTMLDivElement>(null);
  const latestSelectedYearRef = useRef<Dayjs>(targetYear);

  useEffect(() => {
    setLocalYear(targetYear);
    latestSelectedYearRef.current = targetYear;
  }, [targetYear]);

  const handleBlur = (e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>, val?: Dayjs | null) => {
    if (e?.target) {
      const textVal = e.target.value || "";
      const isTextValid = dayjs(textVal, "YYYY", true).isValid();
      if (!isTextValid) {
        setPickerKey((prev) => prev + 1);
        setLocalYear(targetYear);
        return;
      }
    }
    // Selecting a year in the popper triggers onClose -> onBlur() in the same
    // synchronous event, before React re-renders with the new localYear state.
    // latestSelectedYearRef is written synchronously in handleYearChange, so it
    // always holds the freshest selection even in that same-tick case.
    const yearToUse = val || latestSelectedYearRef.current;
    if (!yearToUse || !yearToUse.isValid()) return;
    if (yearToUse.isSame(targetYear, "year")) return;
    onYearChange(yearToUse);
  };

  const handleYearChange = (val: Dayjs | null) => {
    if (val) {
      setLocalYear(val);
      latestSelectedYearRef.current = val;
    }
  };

  const handleYearContainerKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.target.blur();
    }
  }, []);

  const handleYearChevronClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // InputDatePicker only opens on a wrapper click; once open, it ignores further
    // clicks and only closes via its own outside-pointerdown listener (see
    // InputDatePicker.tsx handleWrapperClick / handleOutsidePointerDown). Detect the
    // open calendar popper and dispatch a pointerdown outside it to close, so this
    // chevron toggles instead of only ever opening.
    const isOpen = !!document.querySelector(".MuiPickersPopper-root");
    if (isOpen) {
      document.body.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
      return;
    }
    const inputBase = yearContainerRef.current?.querySelector(".MuiInputBase-root");
    if (inputBase) {
      inputBase.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }
  }, []);

  const handleCategoryChevronClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const select = categoryContainerRef.current?.querySelector(".MuiSelect-select");
    if (select) {
      select.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, button: 0 }));
    }
  }, []);

  return (
    <Stack alignItems="flex-end" gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={2.5}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="cap12" color={black[80]}>
            対象年
          </Typography>
          <DatePickerWithChevron ref={yearContainerRef} onKeyDown={handleYearContainerKeyDown}>
            <InputDatePicker
              key={pickerKey}
              views={["year"]}
              openTo="year"
              format="YYYY"
              value={localYear}
              onChange={handleYearChange}
              onBlur={handleBlur}
              minDate={dayjs("1900/01/01")}
              maxDate={dayjs()}
              enableCloseIcon={false}
              autoClamp
              sx={{ width: "88px", minWidth: "88px", ".MuiInputBase-root": { height: "33px" } }}
            />
            <ChevronIcon onClick={handleYearChevronClick}>
              <IconChevronDown color={black[80]} width={16} height={16} />
            </ChevronIcon>
          </DatePickerWithChevron>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="cap12" color={black[80]}>
            決算月
          </Typography>
          <SelectWithChevron ref={categoryContainerRef}>
            <Select options={categoryOptions} value={selectedCategory} onChange={onCategoryChange} size="md" clearable={false} nonControl />
            <ChevronIcon onClick={handleCategoryChevronClick}>
              <IconChevronDown color={black[80]} width={16} height={16} />
            </ChevronIcon>
          </SelectWithChevron>
        </Stack>
      </Stack>
      <Typography variant="cap12" color={black[80]}>
        {`対象：　${periodLabel}`}
      </Typography>
    </Stack>
  );
};

const DatePickerWithChevron = styled(Box)`
  position: relative;
  display: inline-block;
  width: 96px;
  height: 33px;
  .MuiInputBase-root {
    padding-left: 5px !important;
    padding-right: 28px !important;
  }
  .MuiInputAdornment-root,
  .MuiIconButton-root {
    display: none !important;
  }
`;

const SelectWithChevron = styled(Box)`
  position: relative;
  width: 96px;
  min-width: 88px;
  .MuiInputBase-root {
    height: 33px;
    min-height: 33px;
  }
  /* Override MUI's built-in dropdown-icon padding reserve so the value
     (e.g. "12月") isn't clipped; 28px leaves room for the custom chevron. */
  .MuiSelect-select.MuiSelect-select {
    padding-right: 28px !important;
  }
  .MuiSelect-icon {
    display: none !important;
  }
`;

const ChevronIcon = styled(Box)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  &:hover {
    background-color: ${background.disable};
  }
`;
